import React from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import ProjectPageListTable from "./ProjectPageListTable";
import { TypeEnum } from "../../api/ProjectAPI.types";
import Breadcrumbs from "../../components/utils/Breadcrumbs";
import "../../css/admin/admin.scss";

import type { JSX } from "react";

import type { AppState } from "../../reducers";

interface DefaultPage {
  slug: string;
  icon: string;
  path?: string;
}

type PlatformPage = DefaultPage;

function PlatformPageRow({ slug, icon, path }: DefaultPage): JSX.Element {
  const { t } = useTranslation("editor-platform");
  const history = useHistory();

  return (
    <div
      className="border-b-border bg-card flex size-full cursor-pointer flex-col justify-center border-b"
      data-testid="PlatformPageRow"
    >
      <div
        className="text-muted-foreground hover:bg-muted relative flex h-12.5 w-full flex-nowrap items-center justify-start gap-3.75 px-6.25 text-center whitespace-pre-wrap"
        onClick={(): void => history.push(path ?? `layout/page/${slug}`)}
        role="button"
        tabIndex={0}
      >
        <i className="material-icons">{icon}</i>
        {t(slug)}
      </div>
    </div>
  );
}

export default function PlatformIndex(): JSX.Element {
  const { t } = useTranslation("editor-platform");
  const playlistsType = useSelector(
    (state: AppState) =>
      state?.architectureReducer?.project?.storefront_project_params
        ?.playlists_type,
  );

  const projectType = useSelector(
    (state: AppState) => state.architectureReducer?.project?.type,
  );

  const getPlaylistsPage = (): PlatformPage | null => {
    switch (playlistsType) {
      case "multiple":
        return {
          slug: "my_playlists_page",
          icon: "playlist_play",
        };
      case "single":
        return {
          slug: "my_list_page",
          icon: "playlist_play",
        };
      case "disabled":
      default:
        return null;
    }
  };

  const pageList: (PlatformPage | null)[] = [
    {
      slug: "header_footer",
      icon: "video_label",
    },
    {
      slug: "smartlist_page",
      icon: "folder_special",
    },
    {
      slug: "collection_page",
      icon: "collections_bookmark",
    },
    {
      slug: "content_page",
      icon: "insert_drive_file",
    },
    {
      slug: "casting_page",
      icon: "face",
    },
    projectType !== TypeEnum.Edu
      ? {
          slug: "signup_page",
          icon: "group_add",
        }
      : null,
    getPlaylistsPage(),
    {
      slug: "search_page",
      icon: "search",
    },
    projectType !== TypeEnum.Edu
      ? {
          slug: "offers_page",
          icon: "local_offer",
        }
      : null,
  ];

  const filteredPageList: PlatformPage[] = pageList.filter(
    (page): page is PlatformPage => page !== null,
  );

  const breadcrumbs = [
    {
      name: t("editor-menu:platform"),
      url: "/layout",
    },
    {
      name: t("title.layout"),
    },
  ];

  return (
    <div
      className="editor-container editor-header-simple editor-has-breadcrumbs"
      data-testid="PlatformIndex"
    >
      <Breadcrumbs items={breadcrumbs} />
      <ProjectPageListTable />
      <div className="text-muted-foreground mt-[2.1875rem] mb-[1.09375rem] text-[1.40625rem]">
        {t("title.layout")}
      </div>
      <div className="border-b-border bg-card flex flex-col justify-center border-b drop-shadow-sm">
        {filteredPageList.map((page) => (
          <PlatformPageRow
            key={page.slug}
            icon={page.icon}
            path={page.path}
            slug={page.slug}
          />
        ))}
      </div>
    </div>
  );
}
